/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { Dispatcher } from 'src/core/types/dispatcher';
import { GlobalHandlerGroup } from 'src/core/types/GlobalHandlerGroup';
import { AddToastParameters } from 'src/core/utils/decorators/addToast';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';

const messages = defineMessages({
    addEntityToastStart: {
        id: 'entities.SimpleEntityDetails.addEntityToastStart',
        defaultMessage: 'Adding entity  ...'
    },
    addEntityToastSuccess: {
        id: 'entities.SimpleEntityDetails.addEntityToastSuccess',
        defaultMessage: 'Adding entity completed'
    },
    addEntityToastFailed: {
        id: 'entities.SimpleEntityDetails.addEntityToastFailed',
        defaultMessage: 'Adding entity failed'
    },
    removeEntityToastStart: {
        id: 'entities.SimpleEntityDetails.removeEntityToastStart',
        defaultMessage: 'Removing entity ...'
    },
    removeEntityToastSuccess: {
        id: 'entities.SimpleEntityDetails.removeEntityToastSuccess',
        defaultMessage: 'Removing entity completed'
    },
    removeEntityToastFailed: {
        id: 'entities.SimpleEntityDetails.removeEntityToastFailed',
        defaultMessage: 'Removing entity failed'
    },
    renameEntityToastStart: {
        id: 'entities.SimpleEntityDetails.renameEntityToastStart',
        defaultMessage: 'Renaming entity ...'
    },
    renameEntityToastSuccess: {
        id: 'entities.SimpleEntityDetails.renameEntityToastSuccess',
        defaultMessage: 'Renaming entity completed'
    },
    renameEntityToastFailed: {
        id: 'entities.SimpleEntityDetails.renameEntityToastFailed',
        defaultMessage: 'Renaming entity failed'
    },
    renameChildEntityToastStart: {
        id: 'entities.SimpleEntityDetails.renameChildEntityToastStart',
        defaultMessage: 'Renaming child entity ...'
    },
    renameChildEntityToastSuccess: {
        id: 'entities.SimpleEntityDetails.renameChildEntityToastSuccess',
        defaultMessage: 'Renaming child entity completed'
    },
    renameChildEntityToastFailed: {
        id: 'entities.SimpleEntityDetails.renameChildEntityToastFailed',
        defaultMessage: 'Renaming child entity failed'
    }
});

/**
 * Rename entity helper function.
 *
 * @param entityStore Entity to rename.
 * @param newName New name of the entity.
 * @param dispatcher Handler dispatcher ref.
 * @param tracker Progress tracker.
 * @param toastData Toast data.
 */
const renameEntityHelper = (
    entityStore: EntityStore,
    newName: string,
    dispatcher: Dispatcher<GlobalHandlerGroup>,
    tracker: ProgressTrackerStore,
    toastData: AddToastParameters['toastData']
): void => {
    const payload = { id: entityStore.id, name: newName, toastData, tracker };

    switch (entityStore.typeId) {
        case ModelType.Simple:
            dispatcher.dispatch('renameSimpleEntity', payload);
            break;
        case ModelType.List:
            dispatcher.dispatch('updateListEntity', { ...payload, sublists: (<ListEntityStore>entityStore).sublists });
            break;
        case ModelType.Regex:
            dispatcher.dispatch('updateRegexEntity', { ...payload, pattern: (<RegexEntityStore>entityStore).pattern });
            break;
        case ModelType.Composite:
            dispatcher.dispatch('updateCompositeEntity', { ...payload });
            break;
        case ModelType.Hierarchical:
            dispatcher.dispatch('renameHierarchicalEntity', { ...payload, children: (<HierarchicalEntityStore>entityStore).children });
            break;
        default:
    }
};

export const useEntityTreeActions = () => {
    const { dispatcher } = useGlobalState();
    const { localize } = useLocalization();

    /**
     * Add a new child entity to the entity tree.
     * @param rootEntityId Id of the tree root
     * @param parentEntityId Id of the direct parent of the node being created
     * @param entityName Name of the new entity being created
     */
    const createChildEntity = (rootEntityId: LuisId, parentEntityId: LuisId, entityName: string, tracker?: ProgressTrackerStore) => {
        dispatcher.dispatch('createSimpleChildEntity', {
            parentId: parentEntityId,
            name: entityName,
            absoluteParentId: rootEntityId,
            children: [],
            tracker,
            toastData: {
                running: { title: localize(messages.addEntityToastStart) },
                completed: { title: localize(messages.addEntityToastSuccess) },
                failed: { title: localize(messages.addEntityToastFailed) }
            }
        });
    };

    /**
     * Rename the entity
     *
     * @param entityStore The entity being renamed
     * @param newName The new name for the entity
     */
    const renameEntity = (entityStore: EntityStore, newName: string, tracker?: ProgressTrackerStore) => {
        renameEntityHelper(entityStore, newName, dispatcher, tracker, {
            running: { title: localize(messages.renameEntityToastStart) },
            completed: { title: localize(messages.renameEntityToastSuccess) },
            failed: { title: localize(messages.renameEntityToastFailed) }
        });
    };

    /**
     * [ML entities] Rename the selected child entity
     *
     * @param rootEntityId Id of the tree root
     * @param entityId Id of child node being renamed
     * @param newName The new name for the entity
     */
    const renameChildEntity = (rootEntityId: LuisId, entityId: LuisId, newName: string, tracker?: ProgressTrackerStore) => {
        dispatcher.dispatch('renameSimpleChildEntity', {
            id: entityId,
            name: newName,
            parentId: rootEntityId,
            tracker,
            toastData: {
                running: { title: localize(messages.renameChildEntityToastStart) },
                completed: { title: localize(messages.renameChildEntityToastSuccess) },
                failed: { title: localize(messages.renameChildEntityToastFailed) }
            }
        });
    };

    /**
     * Deletes the selected child entity from the entity tree.
     *
     * @param rootEntityId Id of the tree root
     * @param entityId Id of entity being deleted
     */
    const deleteEntity = (rootEntityId: LuisId, entityId: LuisId, tracker?: ProgressTrackerStore) => {
        // entityId === rootEntityId means it is the root entity
        if (entityId === rootEntityId) {
            dispatcher.dispatch('deleteSimpleEntity', {
                id: entityId,
                tracker,
                toastData: {
                    running: { title: localize(messages.removeEntityToastStart) },
                    completed: { title: localize(messages.removeEntityToastSuccess) },
                    failed: { title: localize(messages.removeEntityToastFailed) }
                }
            });
            // entityId !== rootEntityId means it is a child entity
        } else {
            dispatcher.dispatch('deleteSimpleChildEntity', {
                id: entityId,
                parentId: rootEntityId,
                tracker,
                toastData: {
                    running: { title: localize(messages.removeEntityToastStart) },
                    completed: { title: localize(messages.removeEntityToastSuccess) },
                    failed: { title: localize(messages.removeEntityToastFailed) }
                }
            });
        }
    };

    return { createChildEntity, renameEntity, renameChildEntity, deleteEntity };
};
